/**
 * Frame pacing for the animated background. It caps drawing at 60 fps (so
 * 120 Hz screens don't burn battery on a slow backdrop) and, if the device
 * can't keep up, drops to a steady 30 fps instead of stuttering.
 *
 * It decides from two signals, each over a rolling window of `sample` drawn
 * frames: the interval between drawn frames (catches GPU and compositor load)
 * and the time our own draw call takes (catches slow CPUs). Throttling is one-way for
 * the life of the governor: flapping between rates looks worse than a steady
 * 30 fps, and the background is decoration, not gameplay.
 */
import { mean, median } from '@dropstack/core';

export interface FrameGovernorOptions {
  /** Target rate when the device keeps up. */
  fps: number;
  /** Fallback rate for slow devices. */
  lowFps: number;
  /** Drawn frames to measure before deciding. */
  sample: number;
  /** Median interval (ms) between drawn frames above which we throttle. */
  slowIntervalMs: number;
  /** Mean draw-call cost (ms) above which we throttle. */
  slowDrawMs: number;
}

const DEFAULTS: FrameGovernorOptions = { fps: 60, lowFps: 30, sample: 45, slowIntervalMs: 24, slowDrawMs: 10 };

/** Intervals longer than this are pauses (tab switch, GC, debugger), not slowness. */
const GAP_MS = 250;
/** rAF timestamps jitter by a millisecond or two; don't skip a frame over that. */
const JITTER_MS = 2;

export class FrameGovernor {
  private readonly opts: FrameGovernorOptions;
  private last: number | null = null;
  private intervals: number[] = [];
  private drawCosts: number[] = [];
  private throttled = false;

  constructor(options: Partial<FrameGovernorOptions> = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  /** Current target frame rate. */
  get fps(): number {
    return this.throttled ? this.opts.lowFps : this.opts.fps;
  }

  /** Whether the governor has dropped to the low rate. */
  get isThrottled(): boolean {
    return this.throttled;
  }

  /** Call on every animation frame; returns true when this frame should be drawn. */
  shouldDraw(now: number): boolean {
    if (this.last !== null) {
      const elapsed = now - this.last;
      if (elapsed < 1000 / this.fps - JITTER_MS) return false;
      if (!this.throttled && elapsed < GAP_MS) this.intervals.push(elapsed);
    }
    this.last = now;
    this.evaluate();
    return true;
  }

  /** Report how long the draw call for the last drawn frame took (ms). */
  reportDraw(ms: number): void {
    if (this.throttled || !Number.isFinite(ms)) return;
    this.drawCosts.push(ms);
    this.evaluate();
  }

  /** Forget the last frame time (after a pause), so the gap isn't mistaken for slowness. */
  resume(): void {
    this.last = null;
  }

  private evaluate(): void {
    if (this.throttled) return;
    const { sample, slowIntervalMs, slowDrawMs } = this.opts;
    if (this.intervals.length < sample && this.drawCosts.length < sample) return;
    const slowIntervals = this.intervals.length >= sample && median(this.intervals) > slowIntervalMs;
    const slowDraws = this.drawCosts.length >= sample && mean(this.drawCosts) > slowDrawMs;
    if (slowIntervals || slowDraws) this.throttled = true;
    // Keep a rolling window so a slow patch later in the session is still noticed.
    if (this.intervals.length >= sample) this.intervals = this.intervals.slice(-Math.floor(sample / 2));
    if (this.drawCosts.length >= sample) this.drawCosts = this.drawCosts.slice(-Math.floor(sample / 2));
  }
}
